import Phaser from "phaser";
import { SfxCue } from "../audio/sfx";
import table from "../data/tables/vfx/presets.json";
import { playFloatText } from "./float-text";
import { playLifeLink } from "./play-life-link";
import { playParticles } from "./particle-renderer";
import { playTween } from "./tween-player";
import type { VfxPlayContext, VfxPreset, VfxPresetTable } from "./types";

const data = table as unknown as VfxPresetTable;

function getPreset(id: string): VfxPreset | null {
  const raw = data.presets[id];
  if (!raw) return null;
  return { id, ...raw };
}

function playSfx(name: string | undefined): void {
  if (!name) return;
  const fn = (SfxCue as Record<string, (() => void) | undefined>)[name];
  fn?.();
}

function parseColor(raw: string): number {
  return Number.parseInt(raw.replace(/^0x/i, ""), 16);
}

/**
 * Shared VFX player — features call `play(presetId, ctx)` only.
 * Renderer modules + JSON presets live outside feature folders.
 */
export class VfxPlayer {
  constructor(private readonly scene: Phaser.Scene) {}

  has(id: string): boolean {
    return Boolean(data.presets[id]);
  }

  play(id: string, ctx: VfxPlayContext): void {
    const preset = getPreset(id);
    if (!preset) {
      console.warn(`[vfx] missing preset: ${id}`);
      return;
    }

    playSfx(preset.sfx);

    const resolve = (key: string) => ctx.targets?.[key];

    if (preset.flash) {
      const body = resolve(preset.flash.target) as
        | Phaser.GameObjects.Image
        | Phaser.GameObjects.Arc
        | undefined;
      if (!body) {
        // no-op
      } else if ("setTint" in body && typeof body.setTint === "function") {
        const img = body as Phaser.GameObjects.Image;
        img.setTint(parseColor(preset.flash.color));
        this.scene.time.delayedCall(preset.flash.restoreMs, () => {
          if (img.active) img.clearTint();
        });
      } else if ("setFillStyle" in body) {
        const arc = body as Phaser.GameObjects.Arc;
        const prev = arc.fillColor;
        arc.setFillStyle(parseColor(preset.flash.color), 1);
        this.scene.time.delayedCall(preset.flash.restoreMs, () => {
          if (arc.active) arc.setFillStyle(prev, 1);
        });
      }
    }

    if (preset.link) {
      const x1 = ctx.x1;
      const y1 = ctx.y1;
      if (
        typeof x1 === "number" &&
        typeof y1 === "number" &&
        Number.isFinite(x1) &&
        Number.isFinite(y1)
      ) {
        playLifeLink(
          this.scene,
          { x0: ctx.x, y0: ctx.y, x1, y1 },
          preset.link,
          ctx.depth,
        );
      }
    }

    if (preset.particles) {
      const list = Array.isArray(preset.particles)
        ? preset.particles
        : [preset.particles];
      for (const p of list) {
        playParticles(
          this.scene,
          ctx.x,
          ctx.y,
          p,
          ctx.angle ?? 0,
          ctx.tint,
          ctx.depth,
        );
      }
    }

    if (preset.floatText) {
      playFloatText(
        this.scene,
        ctx.x,
        ctx.y,
        preset.floatText,
        ctx.vars ?? {},
        ctx.floatDepth,
      );
    }

    if (preset.tweens) {
      const victim = resolve("victim") as Phaser.GameObjects.Container | undefined;
      for (const tw of preset.tweens) {
        playTween(this.scene, tw, resolve, {
          lungeToward: victim,
          lungeAngle: ctx.angle,
        });
      }
    }
  }
}

export { listVfxPresetIds } from "./preset-ids";
